"use client"

import Image from "next/image"
import Link from "next/link"
import type { ArticleData } from "@/data/articles"
import type { CmsAuthor } from "@/lib/cms"
import { cmsMediaUrl, cmsLocale } from "@/lib/cms"
import { safeUrl } from "@/lib/url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useParams } from "next/navigation"
import { getBcp47Locale } from "@/i18n/config"
import { ArrowLeftIcon } from "@/components/ui/icons"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/* ───────────────────── Helpers ───────────────────── */

function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  })
}

const CARD_BG: Record<ArticleData["cardColor"], string> = {
  aubergine: "bg-aubergine-900",
  olive: "bg-light-green-900",
  lavender: "bg-[#6B5B8A]",
  white: "bg-white",
}

function isLightCard(color: ArticleData["cardColor"]): boolean {
  return color === "white"
}

function ArticleCard({
  article,
  index: _index,
  locale,
  categoryLabels,
}: {
  article: ArticleData
  index: number
  locale: string
  categoryLabels: Record<string, string>
}) {
  const light = isLightCard(article.cardColor)
  const lp = useLocalePath()

  return (
    <div>
      <Link
        href={lp(`/insights/${article.slug}`)}
        className={`block overflow-hidden rounded-[16px] transition-transform hover:-translate-y-1 ${CARD_BG[article.cardColor]}`}
      >
        <div className="relative aspect-[16/10]">
          <Image
            src={article.heroImage}
            alt={article.title}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="rounded-t-[12px] object-cover"
          />
          <span className="type-body-sm absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 font-mono font-medium tracking-wider text-neutral-700 uppercase backdrop-blur-sm">
            {categoryLabels[article.category] || article.categoryLabel}
          </span>
        </div>
        <div className="px-5 pt-5 pb-5">
          <h3
            className={`text-lg leading-snug font-medium md:text-xl ${light ? "text-aubergine-900" : "text-white"}`}
          >
            {article.title}
          </h3>
          <p
            className={`mt-2 line-clamp-2 text-sm ${light ? "text-neutral-500" : "text-white/70"}`}
          >
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-2.5">
            {article.authorPhoto && (
              <Image
                src={article.authorPhoto}
                alt={article.author}
                width={32}
                height={32}
                className="rounded-full object-cover"
              />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`text-sm ${light ? "text-aubergine-900" : "text-white"}`}
              >
                {article.author}
              </span>
              <span
                className={`text-xs ${light ? "text-neutral-400" : "text-white/50"}`}
              >
                {formatDate(article.date, locale)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  )
}

/* ───────────────────── Page ───────────────────── */

export function AuthorDetailClient({
  cmsAuthor,
  authored,
  reviewed,
}: {
  cmsAuthor: CmsAuthor
  authored: ArticleData[]
  reviewed: ArticleData[]
}) {
  const lp = useLocalePath()
  const locale = useLocale()
  const params = useParams<{ country: string }>()
  const bcp47 = getBcp47Locale(locale, params?.country || "ch")
  // CMS locale this doc was fetched in — the inline editor stamps it so a draft
  // write targets the matching Payload locale row. No-op in public prod.
  const editLocale = cmsLocale(locale, useCountry().code)
  const authorId = String(cmsAuthor.id)
  const nameRef = useEditRegion<HTMLHeadingElement>(
    authorId
      ? {
          collection: "authors",
          id: authorId,
          field: "name",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const roleRef = useEditRegion<HTMLParagraphElement>(
    authorId
      ? {
          collection: "authors",
          id: authorId,
          field: "role",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const bioRef = useEditRegion<HTMLParagraphElement>(
    authorId
      ? {
          collection: "authors",
          id: authorId,
          field: "bio",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const t = useTranslations("ui")
  // The "all authors" listing is gated per market (page:authors). Austria
  // keeps the bio pages but not the listing, so the back link would 404 there.
  const showAllAuthorsLink = isAllowedInCountry(
    "page:authors",
    useCountry().code
  )
  const ti = useTranslations("insights")
  const categoryLabels: Record<string, string> = {
    "early-detection": ti("earlyDetection"),
    technology: ti("technology"),
    "nutrition-lifestyle": ti("nutritionLifestyle"),
    "cost-insurance": ti("costInsurance"),
  }

  const displayName = cmsAuthor.name
  const displayRole = cmsAuthor.role
  const displayBio = cmsAuthor.bio || ""
  const displayPhoto = cmsAuthor.photo
    ? cmsMediaUrl(cmsAuthor.photo.url)
    : undefined
  const displayCompanies = cmsAuthor.companies ?? []

  return (
    <>
      <section className="px-6 pt-12 pb-16 md:pt-16">
        <div className="mx-auto max-w-7xl">
          {/* Breadcrumb */}
          {showAllAuthorsLink && (
            <div>
              <Link
                href={lp("/authors")}
                className="hover:text-aubergine-900 inline-flex items-center gap-1 text-sm text-neutral-400 transition-colors"
              >
                <ArrowLeftIcon aria-hidden="true" />
                <span className="type-body-sm font-mono font-medium tracking-[0.15em] uppercase">
                  {t("authorsBackLink")}
                </span>
              </Link>
            </div>
          )}

          {/* Author header */}
          <div>
            <div className="mt-8 flex flex-col items-start gap-6 sm:flex-row">
              {displayPhoto ? (
                <Image
                  src={displayPhoto}
                  alt={displayName}
                  width={128}
                  height={128}
                  className="h-32 w-32 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-full bg-neutral-200 text-4xl font-medium text-neutral-400">
                  {displayName.charAt(0)}
                </div>
              )}
              <div>
                <h1
                  ref={nameRef}
                  className="text-aubergine-900 text-3xl font-bold"
                >
                  {displayName}
                </h1>
                <p ref={roleRef} className="mt-1 text-base text-neutral-500">
                  {displayRole}
                </p>
                {cmsAuthor?.credentials && (
                  <p className="mt-0.5 text-sm text-neutral-400">
                    {cmsAuthor.credentials}
                  </p>
                )}
                <p
                  ref={bioRef}
                  className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-500"
                >
                  {displayBio}
                </p>

                {cmsAuthor?.linkedinUrl &&
                  safeUrl(cmsAuthor.linkedinUrl) !== "#" && (
                    <a
                      href={safeUrl(cmsAuthor.linkedinUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-aubergine-900 mt-3 inline-flex items-center gap-1.5 text-sm text-neutral-400 transition-colors"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      LinkedIn
                    </a>
                  )}

                {/* Company logos */}
                {displayCompanies.length > 0 && (
                  <div className="mt-4 flex items-center gap-6">
                    {displayCompanies.map((company) =>
                      company.logo &&
                      typeof company.logo === "object" &&
                      company.logo.url ? (
                        <Image
                          key={company.name}
                          src={cmsMediaUrl(company.logo.url)}
                          alt={company.logo.alt || company.name}
                          width={100}
                          height={24}
                          className="h-6 w-auto max-w-[100px] object-contain brightness-0"
                        />
                      ) : (
                        <span
                          key={company.name}
                          className="text-lg font-bold tracking-wide text-neutral-300"
                        >
                          {company.name}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Articles written by author */}
          {authored.length > 0 && (
            <>
              <div className="mt-12">
                <h2 className="text-aubergine-900 text-2xl font-normal md:text-3xl">
                  {t("authorsArticlesBy", { name: displayName.split(" ")[0] })}
                </h2>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {authored.map((article, index) => (
                  <ArticleCard
                    key={article.slug}
                    article={article}
                    index={index}
                    locale={bcp47}
                    categoryLabels={categoryLabels}
                  />
                ))}
              </div>
            </>
          )}

          {/* Articles reviewed by author */}
          {reviewed.length > 0 && (
            <>
              <div className="mt-12">
                <h2 className="text-aubergine-900 text-2xl font-normal md:text-3xl">
                  {t("authorsReviewedBy", { name: displayName })}
                </h2>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {reviewed.map((article, index) => (
                  <ArticleCard
                    key={article.slug}
                    article={article}
                    index={index}
                    locale={bcp47}
                    categoryLabels={categoryLabels}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}
